import { isNil } from 'lodash';

import S from 'constants/string.const';
import fromAnyToString from 'utils/converters/fromAnyToString';
import nullBind from 'utils/functions/nullBind.util';
import withOptions from 'utils/functions/withOptions.util';
import currency from 'utils/intl/currency.util';
import currencyFixed from 'utils/intl/currencyFixed.util';
import logger from 'utils/logger.util';
import isEnumVal from 'utils/predicates/enum/isEnumVal.util';

const L = logger('currencySign');

const USE_NATIVE = false;
const RE_ALL_MINUSES = /-/g;
const DEFAULT_FRACTION = 2;

const MAP = Object.freeze({
  AED: 'د.إ',
  EUR: '€',
  GBP: '£',
  USD: '$',
});

const MINUS_POSITION = Object.freeze({
  first: 'first',
  inner: 'inner',
  last: 'last',
});
const DEFAULT_MINUS_POSITION = MINUS_POSITION.first;

const isMinusPos = nullBind(isEnumVal, MINUS_POSITION);

const format = ({ minus, sigil, fixed }) => {
  // NOTE: fixed will be a string, for a reason, so use string operations, don't bother parsing into number again

  const currency = fromAnyToString(sigil);

  // number shouldn't start with a minus, unless in scientific notation which should not be used though
  const isNegative = fixed.startsWith(S.minus);
  if (!isNegative) {
    return currency + fixed;
  }

  const value = fixed.replace(RE_ALL_MINUSES, S.empty);
  const position = isMinusPos(minus) ? minus : DEFAULT_MINUS_POSITION;

  if (position === MINUS_POSITION.first) {
    return S.minus + currency + value;
  }

  if (position === MINUS_POSITION.inner) {
    return currency + S.minus + value;
  }

  if (position === MINUS_POSITION.last) {
    return currency + value + S.minus;
  }

  // same as  MINUS_POSITION.first due to DEFAULT_MINUS_POSITION, and it shouldn't come this far
  return L.warn('format()', 'Unhandled case:', { minus, sigil, fixed }, 'result:', S.minus + currency + value);
};

const currencySign = withOptions((options) => {
  const { value, currentSalonSign, currentSalonAbbr, sign, abbr, native, fraction, minus } = options;

  const fixed = currencyFixed({ value, fraction: isNil(fraction) ? DEFAULT_FRACTION : fraction });

  if (USE_NATIVE && native && fixed !== '') {
    // NOTE: don't modify the native result, it's what the browser/OS determined is right, even if it isn't or is buggy
    // NOTE: not actual locale, but currency() utility does some limited translation
    const locale = currentSalonAbbr || abbr;
    const isSymbol = typeof value === 'symbol';
    return currency({ value: isSymbol ? '' : value, abbr: locale, locale });
  }

  if (currentSalonSign) {
    return format({ minus, fixed, sigil: currentSalonSign });
  }

  if (currentSalonAbbr) {
    return format({ minus, fixed, sigil: MAP[currentSalonAbbr] });
  }

  if (abbr) {
    return format({ minus, fixed, sigil: MAP[abbr] });
  }

  return format({ minus, fixed, sigil: sign });
});

// noinspection JSUnusedGlobalSymbols
export default currencySign;
